/**
 * The account page's big number: sales for the picked period (month or year
 * to date), the change vs. last year under it, then the MTD | YTD picker and the
 * black bar.
 */

import { useTranslation } from "react-i18next";
import type { SalesInsightsAccountResponse } from "@onsemble/shared/types/sales-insights";
import { dollars, growth } from "../../format";
import { CHANGE_TONE, changeTone } from "../../shared/changeTone";
import { PeriodTabs, type Period } from "../../shared/PeriodTabs";

export function Hero({
  account,
  period,
  onPeriodChange,
}: {
  account: SalesInsightsAccountResponse;
  /** Month to date or year to date */
  period: Period;
  onPeriodChange: (next: Period) => void;
}) {
  const { t } = useTranslation();
  const year = period === "year";
  // The period's sales and change
  const sales = year ? account.salesYtd : account.salesMtd;
  // Arrow and color for the change, by the shared ±0.5% rule
  const tone = CHANGE_TONE[changeTone(sales.growth)];

  return (
    <div className="mt-8">
      {/* Label */}
      <div className="text-si-sm font-medium text-si-ink-2">
        {t(year ? "pages.pcInsights.summary.salesYtd" : "pages.pcInsights.summary.salesMtd")}
      </div>
      {/* The big number: sales for the period */}
      <div className="mt-1 text-[34px] font-medium leading-[1.05] tracking-[-0.03em] tabular-nums">
        {dollars(sales.sales)}
      </div>
      {/* Change vs. last year; a grey dash when there's no prior year */}
      <div className={`mt-1.5 text-si-md tabular-nums ${tone.className}`}>
        {tone.glyph}
        {growth(sales.growth)}
      </div>
      {/* MTD | YTD picker with the black bar centered under it, as a divider */}
      <div className="inline-flex flex-col items-center">
        <PeriodTabs value={period} onChange={onPeriodChange} />
        {/* Short black bar centered under the picker */}
        <div aria-hidden="true" className="mt-3 h-[3px] w-full rounded-full bg-si-ink" />
      </div>
    </div>
  );
}
